import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Brain, Puzzle, BookHeart, Grid3x3, ArrowLeft, Volume2, Gamepad2, Music, Eye, Hand } from 'lucide-react';
import { useGame } from '../context/GameContext';

const GameFormatSelection = () => {
  const navigate = useNavigate();
  const { setSelectedFormat, selectedSubject, selectedAge, playerName } = useGame();
  const [hoveredFormat, setHoveredFormat] = useState(null);
  const [playingSound, setPlayingSound] = useState(null);

  // Cancel speech on unmount
  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  const playSound = (text) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.pitch = 1.2;
    utterance.volume = 0.8;
    window.speechSynthesis.speak(utterance);
  };

  // Topic-specific configurations
  const topicConfig = {
    // Little Explorer (3-5 years) topics
    'abc-adventures': {
      title: 'ABC Adventures',
      emoji: '📚',
      subtitle: 'Learn Letters & Sounds!',
      bgGradient: 'linear-gradient(135deg, #E879F9 0%, #F0ABFC 100%)',
      formats: [
        {
          id: 'quiz',
          label: 'Letter Quiz',
          icon: Brain,
          route: '/game/quiz',
          color: 'from-purple-400 to-pink-400',
          bgColor: '#E879F9',
          emoji: '🔤',
          description: 'Which letter is this? A, B, or C?',
          sound: 'Let\'s play the letter quiz! Can you find the right letter?'
        },
        {
          id: 'memory',
          label: 'Letter Match',
          icon: Grid3x3,
          route: '/game/memory',
          color: 'from-pink-400 to-rose-400',
          bgColor: '#FB7185',
          emoji: '🎴',
          description: 'Match uppercase and lowercase letters!',
          sound: 'Memory game! Match the big and small letters!'
        },
        {
          id: 'puzzle',
          label: 'ABC Puzzle',
          icon: Puzzle,
          route: '/game/puzzle',
          color: 'from-fuchsia-400 to-purple-400',
          bgColor: '#C026D3',
          emoji: '🧩',
          description: 'Put the letters in the right order!',
          sound: 'Puzzle time! Put A, B, C in order!'
        },
        {
          id: 'story',
          label: 'ABC Story',
          icon: BookHeart,
          route: '/game/story',
          color: 'from-violet-400 to-purple-400',
          bgColor: '#8B5CF6',
          emoji: '📖',
          description: 'Learn letters through fun stories!',
          sound: 'Story time! A is for Apple, B is for Ball!'
        }
      ]
    },
    'counting-fun': {
      title: 'Counting Fun',
      emoji: '🔢',
      subtitle: 'Play with Numbers!',
      bgGradient: 'linear-gradient(135deg, #38BDF8 0%, #7DD3FC 100%)',
      formats: [
        {
          id: 'quiz',
          label: 'Number Quiz',
          icon: Brain,
          route: '/game/quiz',
          color: 'from-blue-400 to-cyan-400',
          bgColor: '#38BDF8',
          emoji: '🔢',
          description: 'How many apples do you see?',
          sound: 'Count with me! 1, 2, 3! How many do you see?'
        },
        {
          id: 'memory',
          label: 'Number Match',
          icon: Grid3x3,
          route: '/game/memory',
          color: 'from-cyan-400 to-teal-400',
          bgColor: '#22D3EE',
          emoji: '🎯',
          description: 'Match numbers with pictures!',
          sound: 'Find the matching numbers!'
        },
        {
          id: 'puzzle',
          label: 'Count & Sort',
          icon: Puzzle,
          route: '/game/puzzle',
          color: 'from-sky-400 to-blue-400',
          bgColor: '#0EA5E9',
          emoji: '📊',
          description: 'Put numbers in the right order!',
          sound: 'Sort the numbers! 1 comes first, then 2, then 3!'
        },
        {
          id: 'story',
          label: 'Number Story',
          icon: BookHeart,
          route: '/game/story',
          color: 'from-indigo-400 to-blue-400',
          bgColor: '#6366F1',
          emoji: '📚',
          description: 'Count along with fun stories!',
          sound: 'Story time! Once upon a time, there were 3 little pigs!'
        }
      ]
    },
    'colors-shapes': {
      title: 'Colors & Shapes',
      emoji: '🎨',
      subtitle: 'Explore Colors & Shapes!',
      bgGradient: 'linear-gradient(135deg, #FB923C 0%, #FDBA74 100%)',
      formats: [
        {
          id: 'quiz',
          label: 'Color Quiz',
          icon: Brain,
          route: '/game/quiz',
          color: 'from-orange-400 to-red-400',
          bgColor: '#F97316',
          emoji: '🌈',
          description: 'What color is this? Red, blue, or yellow?',
          sound: 'What color do you see? Is it red, blue, or yellow?'
        },
        {
          id: 'memory',
          label: 'Shape Match',
          icon: Grid3x3,
          route: '/game/memory',
          color: 'from-red-400 to-pink-400',
          bgColor: '#EF4444',
          emoji: '🔷',
          description: 'Match the shapes! Circle, square, triangle!',
          sound: 'Find the matching shapes!'
        },
        {
          id: 'puzzle',
          label: 'Color Puzzle',
          icon: Puzzle,
          route: '/game/puzzle',
          color: 'from-amber-400 to-orange-400',
          bgColor: '#F59E0B',
          emoji: '🧩',
          description: 'Sort by colors and shapes!',
          sound: 'Sort the colors! Red goes here, blue goes there!'
        },
        {
          id: 'story',
          label: 'Rainbow Story',
          icon: BookHeart,
          route: '/game/story',
          color: 'from-yellow-400 to-orange-400',
          bgColor: '#EAB308',
          emoji: '🌈',
          description: 'Learn colors through rainbow adventures!',
          sound: 'Let\'s learn about the rainbow! Red, orange, yellow, green, blue!'
        }
      ]
    },
    'animal-friends': {
      title: 'Animal Friends',
      emoji: '🐶',
      subtitle: 'Meet Cute Animals!',
      bgGradient: 'linear-gradient(135deg, #4ADE80 0%, #86EFAC 100%)',
      formats: [
        {
          id: 'quiz',
          label: 'Animal Quiz',
          icon: Brain,
          route: '/game/quiz',
          color: 'from-green-400 to-emerald-400',
          bgColor: '#22C55E',
          emoji: '🦁',
          description: 'What animal makes this sound?',
          sound: 'What animal says Moo? Is it a cow, a dog, or a cat?'
        },
        {
          id: 'memory',
          label: 'Animal Match',
          icon: Grid3x3,
          route: '/game/memory',
          color: 'from-emerald-400 to-teal-400',
          bgColor: '#10B981',
          emoji: '🐾',
          description: 'Match baby animals to their mommies!',
          sound: 'Find the matching animals!'
        },
        {
          id: 'puzzle',
          label: 'Animal Puzzle',
          icon: Puzzle,
          route: '/game/puzzle',
          color: 'from-lime-400 to-green-400',
          bgColor: '#84CC16',
          emoji: '🧩',
          description: 'Put the animal picture together!',
          sound: 'Build the animal puzzle! What animal is it?'
        },
        {
          id: 'story',
          label: 'Animal Story',
          icon: BookHeart,
          route: '/game/story',
          color: 'from-teal-400 to-cyan-400',
          bgColor: '#14B8A6',
          emoji: '📖',
          description: 'Fun stories about animals!',
          sound: 'Story time! The dog says woof, the cat says meow!'
        }
      ]
    },
    'safety-first': {
      title: 'Safety First',
      emoji: '🛡️',
      subtitle: 'Learn to Stay Safe!',
      bgGradient: 'linear-gradient(135deg, #FBBF24 0%, #FDE047 100%)',
      formats: [
        {
          id: 'quiz',
          label: 'Safety Quiz',
          icon: Brain,
          route: '/game/quiz',
          color: 'from-yellow-400 to-amber-400',
          bgColor: '#F59E0B',
          emoji: '✅',
          description: 'Is this safe or not safe?',
          sound: 'Safety quiz! Should you look both ways before crossing?'
        },
        {
          id: 'memory',
          label: 'Safety Signs',
          icon: Grid3x3,
          route: '/game/memory',
          color: 'from-amber-400 to-orange-400',
          bgColor: '#D97706',
          emoji: '🚸',
          description: 'Match safety signs!',
          sound: 'Match the safety signs! Stop sign, crosswalk sign!'
        },
        {
          id: 'puzzle',
          label: 'Safe Choices',
          icon: Puzzle,
          route: '/game/puzzle',
          color: 'from-orange-400 to-red-400',
          bgColor: '#EA580C',
          emoji: '🤔',
          description: 'Pick the safe choice!',
          sound: 'Which is the safe choice? Think carefully!'
        },
        {
          id: 'story',
          label: 'Safety Story',
          icon: BookHeart,
          route: '/game/story',
          color: 'from-yellow-500 to-amber-500',
          bgColor: '#EAB308',
          emoji: '📚',
          description: 'Learn safety through stories!',
          sound: 'Story time! Always hold hands when crossing the street!'
        }
      ]
    },
    'transportation': {
      title: 'Transportation',
      emoji: '🚗',
      subtitle: 'Cars, Trains & Planes!',
      bgGradient: 'linear-gradient(135deg, #A78BFA 0%, #C4B5FD 100%)',
      formats: [
        {
          id: 'quiz',
          label: 'Vehicle Quiz',
          icon: Brain,
          route: '/game/quiz',
          color: 'from-violet-400 to-purple-400',
          bgColor: '#8B5CF6',
          emoji: '🚁',
          description: 'What vehicle is this?',
          sound: 'What vehicle goes vroom? Is it a car, a train, or a plane?'
        },
        {
          id: 'memory',
          label: 'Vehicle Match',
          icon: Grid3x3,
          route: '/game/memory',
          color: 'from-purple-400 to-fuchsia-400',
          bgColor: '#A855F7',
          emoji: '🎴',
          description: 'Match vehicles and their sounds!',
          sound: 'Match the vehicles! Car goes vroom, train goes choo choo!'
        },
        {
          id: 'puzzle',
          label: 'Build a Vehicle',
          icon: Puzzle,
          route: '/game/puzzle',
          color: 'from-indigo-400 to-violet-400',
          bgColor: '#6366F1',
          emoji: '🔧',
          description: 'Put the vehicle together!',
          sound: 'Build the vehicle! Add the wheels, add the windows!'
        },
        {
          id: 'story',
          label: 'Travel Story',
          icon: BookHeart,
          route: '/game/story',
          color: 'from-fuchsia-400 to-pink-400',
          bgColor: '#D946EF',
          emoji: '✈️',
          description: 'Adventures with vehicles!',
          sound: 'Story time! The little train went choo choo up the mountain!'
        }
      ]
    },
    'reading': {
      title: 'Reading Time',
      emoji: '📖',
      subtitle: 'Stories & Adventures!',
      bgGradient: 'linear-gradient(135deg, #FB7185 0%, #FDA4AF 100%)',
      formats: [
        {
          id: 'quiz',
          label: 'Word Quiz',
          icon: Brain,
          route: '/game/quiz',
          color: 'from-rose-400 to-pink-400',
          bgColor: '#F43F5E',
          emoji: '📝',
          description: 'Can you read this word?',
          sound: 'Reading quiz! Can you read C-A-T? Cat!'
        },
        {
          id: 'memory',
          label: 'Word Match',
          icon: Grid3x3,
          route: '/game/memory',
          color: 'from-pink-400 to-rose-400',
          bgColor: '#EC4899',
          emoji: '🎴',
          description: 'Match words to pictures!',
          sound: 'Match the word to the picture! Dog matches the puppy!'
        },
        {
          id: 'puzzle',
          label: 'Word Puzzle',
          icon: Puzzle,
          route: '/game/puzzle',
          color: 'from-red-400 to-rose-400',
          bgColor: '#EF4444',
          emoji: '🧩',
          description: 'Build words letter by letter!',
          sound: 'Build the word! C-A-T spells cat!'
        },
        {
          id: 'story',
          label: 'Story Time',
          icon: BookHeart,
          route: '/game/story',
          color: 'from-rose-500 to-pink-500',
          bgColor: '#BE185D',
          emoji: '📚',
          description: 'Read along with fun stories!',
          sound: 'Story time! Once upon a time, in a land far away...'
        }
      ]
    },
    'nature-outdoors': {
      title: 'Nature & Outdoors',
      emoji: '🌳',
      subtitle: 'Explore the World!',
      bgGradient: 'linear-gradient(135deg, #84CC16 0%, #BEF264 100%)',
      formats: [
        {
          id: 'quiz',
          label: 'Nature Quiz',
          icon: Brain,
          route: '/game/quiz',
          color: 'from-lime-400 to-green-400',
          bgColor: '#84CC16',
          emoji: '🌸',
          description: 'What flower is this?',
          sound: 'Nature quiz! What color is the sunflower? Yellow!'
        },
        {
          id: 'memory',
          label: 'Nature Match',
          icon: Grid3x3,
          route: '/game/memory',
          color: 'from-green-400 to-lime-400',
          bgColor: '#22C55E',
          emoji: '🦋',
          description: 'Match plants and animals!',
          sound: 'Match the nature items! Bee goes with flower!'
        },
        {
          id: 'puzzle',
          label: 'Garden Puzzle',
          icon: Puzzle,
          route: '/game/puzzle',
          color: 'from-emerald-400 to-green-400',
          bgColor: '#10B981',
          emoji: '🌻',
          description: 'Build a beautiful garden!',
          sound: 'Build the garden! Plant the flowers!'
        },
        {
          id: 'story',
          label: 'Nature Story',
          icon: BookHeart,
          route: '/game/story',
          color: 'from-teal-400 to-emerald-400',
          bgColor: '#14B8A6',
          emoji: '🌲',
          description: 'Adventures in nature!',
          sound: 'Story time! The little seed grew into a big tree!'
        }
      ]
    },
    'daily-activities': {
      title: 'Daily Activities',
      emoji: '🌅',
      subtitle: 'Learn Your Routine!',
      bgGradient: 'linear-gradient(135deg, #38BDF8 0%, #7DD3FC 100%)',
      formats: [
        {
          id: 'quiz',
          label: 'Routine Quiz',
          icon: Brain,
          route: '/game/quiz',
          color: 'from-sky-400 to-blue-400',
          bgColor: '#0EA5E9',
          emoji: '⏰',
          description: 'What do you do in the morning?',
          sound: 'Daily quiz! What do you do first when you wake up?'
        },
        {
          id: 'memory',
          label: 'Activity Match',
          icon: Grid3x3,
          route: '/game/memory',
          color: 'from-blue-400 to-sky-400',
          bgColor: '#3B82F6',
          emoji: '🎴',
          description: 'Match activities to times!',
          sound: 'Match the activities! Breakfast is in the morning!'
        },
        {
          id: 'puzzle',
          label: 'Day Puzzle',
          icon: Puzzle,
          route: '/game/puzzle',
          color: 'from-cyan-400 to-sky-400',
          bgColor: '#06B6D4',
          emoji: '📅',
          description: 'Put your day in order!',
          sound: 'Order your day! First wake up, then brush teeth!'
        },
        {
          id: 'story',
          label: 'My Day Story',
          icon: BookHeart,
          route: '/game/story',
          color: 'from-indigo-400 to-blue-400',
          bgColor: '#6366F1',
          emoji: '🌞',
          description: 'A day in the life story!',
          sound: 'Story time! Good morning! Time to start a new day!'
        }
      ]
    },
    'food': {
      title: 'Yummy Food',
      emoji: '🍎',
      subtitle: 'Healthy & Delicious!',
      bgGradient: 'linear-gradient(135deg, #F87171 0%, #FCA5A5 100%)',
      formats: [
        {
          id: 'quiz',
          label: 'Food Quiz',
          icon: Brain,
          route: '/game/quiz',
          color: 'from-red-400 to-rose-400',
          bgColor: '#EF4444',
          emoji: '🍓',
          description: 'What fruit is this?',
          sound: 'Food quiz! What fruit is red and yummy? Strawberry!'
        },
        {
          id: 'memory',
          label: 'Food Match',
          icon: Grid3x3,
          route: '/game/memory',
          color: 'from-rose-400 to-pink-400',
          bgColor: '#F43F5E',
          emoji: '🎴',
          description: 'Match fruits and vegetables!',
          sound: 'Match the foods! Apple matches apple!'
        },
        {
          id: 'puzzle',
          label: 'Healthy Plate',
          icon: Puzzle,
          route: '/game/puzzle',
          color: 'from-orange-400 to-red-400',
          bgColor: '#F97316',
          emoji: '🍽️',
          description: 'Build a healthy meal!',
          sound: 'Build a healthy plate! Add fruits and vegetables!'
        },
        {
          id: 'story',
          label: 'Food Story',
          icon: BookHeart,
          route: '/game/story',
          color: 'from-amber-400 to-orange-400',
          bgColor: '#F59E0B',
          emoji: '👨‍🍳',
          description: 'Cooking adventures!',
          sound: 'Story time! The little chef made a yummy salad!'
        }
      ]
    },
    // Art & Creativity (Young Scholar)
    'art-creativity': {
      title: 'Art & Creativity',
      emoji: '🎨',
      subtitle: 'Express Yourself!',
      bgGradient: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)',
      formats: [
        {
          id: 'quiz',
          label: 'Creative Challenge',
          icon: Brain,
          route: '/game/quiz',
          color: 'from-pink-400 to-fuchsia-400',
          bgColor: '#EC4899',
          emoji: '💡',
          description: 'Think outside the box with creative puzzles!',
          sound: 'Creative challenge! Use your imagination!'
        },
        {
          id: 'puzzle',
          label: 'Art Appreciation',
          icon: Puzzle,
          route: '/game/puzzle',
          color: 'from-fuchsia-400 to-purple-400',
          bgColor: '#D946EF',
          emoji: '🖼️',
          description: 'Learn about famous art and artists!',
          sound: 'Art appreciation! Let\'s explore beautiful artwork!'
        },
        {
          id: 'memory',
          label: 'Color Match',
          icon: Grid3x3,
          route: '/game/memory',
          color: 'from-purple-400 to-pink-400',
          bgColor: '#A855F7',
          emoji: '🌈',
          description: 'Match colors and patterns!',
          sound: 'Match the colors! Red, blue, yellow!'
        },
        {
          id: 'story',
          label: 'Art Story',
          icon: BookHeart,
          route: '/game/story',
          color: 'from-rose-400 to-pink-400',
          bgColor: '#F43F5E',
          emoji: '✨',
          description: 'Stories about creativity and imagination!',
          sound: 'Story time! Once there was an artist who painted dreams!'
        }
      ]
    },
    // Health & Wellness (Young Scholar)
    'health-wellness': {
      title: 'Health & Wellness',
      emoji: '💪',
      subtitle: 'Stay Healthy & Strong!',
      bgGradient: 'linear-gradient(135deg, #EF4444 0%, #F87171 100%)',
      formats: [
        {
          id: 'quiz',
          label: 'Nutrition Quiz',
          icon: Brain,
          route: '/game/quiz',
          color: 'from-red-400 to-orange-400',
          bgColor: '#EF4444',
          emoji: '🥗',
          description: 'Learn about healthy foods and nutrition!',
          sound: 'Nutrition quiz! What foods make us strong and healthy?'
        },
        {
          id: 'puzzle',
          label: 'Healthy Habits',
          icon: Puzzle,
          route: '/game/puzzle',
          color: 'from-orange-400 to-amber-400',
          bgColor: '#F97316',
          emoji: '🏃',
          description: 'Build good habits for a healthy life!',
          sound: 'Healthy habits! Exercise, sleep, and eat well!'
        },
        {
          id: 'memory',
          label: 'Food Groups',
          icon: Grid3x3,
          route: '/game/memory',
          color: 'from-green-400 to-emerald-400',
          bgColor: '#22C55E',
          emoji: '🍎',
          description: 'Match healthy foods and food groups!',
          sound: 'Match the food groups! Fruits, vegetables, proteins!'
        },
        {
          id: 'story',
          label: 'Wellness Story',
          icon: BookHeart,
          route: '/game/story',
          color: 'from-teal-400 to-cyan-400',
          bgColor: '#14B8A6',
          emoji: '❤️',
          description: 'Stories about staying healthy!',
          sound: 'Story time! Learn how to take care of your body!'
        }
      ]
    },
    // Financial Literacy (Smart Champion 9-12)
    'financial-literacy': {
      title: 'Financial Literacy',
      emoji: '💰',
      subtitle: 'Learn About Money!',
      bgGradient: 'linear-gradient(135deg, #22C55E 0%, #4ADE80 100%)',
      formats: [
        {
          id: 'quiz',
          label: 'Money Quiz',
          icon: Brain,
          route: '/game/quiz',
          color: 'from-green-400 to-emerald-400',
          bgColor: '#22C55E',
          emoji: '💵',
          description: 'Saving, spending, and budgeting basics!',
          sound: 'Money quiz! Learn how to save and spend wisely!'
        },
        {
          id: 'puzzle',
          label: 'Needs vs Wants',
          icon: Puzzle,
          route: '/game/puzzle',
          color: 'from-emerald-400 to-teal-400',
          bgColor: '#10B981',
          emoji: '🤔',
          description: 'Can you tell the difference?',
          sound: 'Needs versus wants! Which is which?'
        },
        {
          id: 'memory',
          label: 'Budget Match',
          icon: Grid3x3,
          route: '/game/memory',
          color: 'from-teal-400 to-cyan-400',
          bgColor: '#14B8A6',
          emoji: '📊',
          description: 'Match money concepts!',
          sound: 'Match the money concepts! Saving, spending, investing!'
        },
        {
          id: 'story',
          label: 'Entrepreneur Story',
          icon: BookHeart,
          route: '/game/story',
          color: 'from-lime-400 to-green-400',
          bgColor: '#84CC16',
          emoji: '🚀',
          description: 'Stories of young entrepreneurs!',
          sound: 'Story time! Learn about starting your own business!'
        }
      ]
    },
    // World Explorer / Geography (for all age groups)
    'geography': {
      title: 'World Explorer',
      emoji: '🗺️',
      subtitle: 'Explore Our Amazing World!',
      bgGradient: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
      formats: [
        {
          id: 'quiz',
          label: 'Geography Quiz',
          icon: Brain,
          route: '/game/quiz',
          color: 'from-emerald-400 to-teal-400',
          bgColor: '#10B981',
          emoji: '🌍',
          description: 'Countries, continents, and capitals!',
          sound: 'Geography quiz! How well do you know our world?'
        },
        {
          id: 'puzzle',
          label: 'Map Challenge',
          icon: Puzzle,
          route: '/game/puzzle',
          color: 'from-teal-400 to-cyan-400',
          bgColor: '#14B8A6',
          emoji: '🗺️',
          description: 'Navigate maps and locations!',
          sound: 'Map challenge! Can you find the countries?'
        },
        {
          id: 'memory',
          label: 'Country Match',
          icon: Grid3x3,
          route: '/game/memory',
          color: 'from-cyan-400 to-sky-400',
          bgColor: '#06B6D4',
          emoji: '🏳️',
          description: 'Match countries and flags!',
          sound: 'Match the countries with their flags!'
        },
        {
          id: 'story',
          label: 'World Stories',
          icon: BookHeart,
          route: '/game/story',
          color: 'from-green-400 to-emerald-400',
          bgColor: '#22C55E',
          emoji: '✈️',
          description: 'Adventures around the world!',
          sound: 'Story time! Travel the world with exciting adventures!'
        }
      ]
    },
    // History Quest (for all age groups)
    'history': {
      title: 'History Quest',
      emoji: '📜',
      subtitle: 'Journey Through Time!',
      bgGradient: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
      formats: [
        {
          id: 'quiz',
          label: 'History Quiz',
          icon: Brain,
          route: '/game/quiz',
          color: 'from-amber-400 to-yellow-400',
          bgColor: '#F59E0B',
          emoji: '🏛️',
          description: 'Ancient civilizations and famous events!',
          sound: 'History quiz! How well do you know the past?'
        },
        {
          id: 'puzzle',
          label: 'Timeline Challenge',
          icon: Puzzle,
          route: '/game/puzzle',
          color: 'from-yellow-400 to-orange-400',
          bgColor: '#EAB308',
          emoji: '⏳',
          description: 'Put events in chronological order!',
          sound: 'Timeline challenge! What happened first?'
        },
        {
          id: 'memory',
          label: 'History Match',
          icon: Grid3x3,
          route: '/game/memory',
          color: 'from-orange-400 to-amber-400',
          bgColor: '#F97316',
          emoji: '🏰',
          description: 'Match historical figures and events!',
          sound: 'Match the historical figures!'
        },
        {
          id: 'story',
          label: 'History Stories',
          icon: BookHeart,
          route: '/game/story',
          color: 'from-amber-500 to-yellow-500',
          bgColor: '#D97706',
          emoji: '📖',
          description: 'Amazing stories from the past!',
          sound: 'Story time! Discover incredible moments in history!'
        }
      ]
    },
    // Social-Emotional Learning (Smart Champion 9-12)
    'social-emotional': {
      title: 'Social-Emotional Learning',
      emoji: '🤝',
      subtitle: 'Build Amazing Relationships!',
      bgGradient: 'linear-gradient(135deg, #0EA5E9 0%, #38BDF8 100%)',
      formats: [
        {
          id: 'quiz',
          label: 'Empathy Quiz',
          icon: Brain,
          route: '/game/quiz',
          color: 'from-sky-400 to-blue-400',
          bgColor: '#0EA5E9',
          emoji: '💙',
          description: 'Empathy and understanding others!',
          sound: 'Empathy quiz! How well do you understand others feelings?'
        },
        {
          id: 'puzzle',
          label: 'Goal Setting',
          icon: Puzzle,
          route: '/game/puzzle',
          color: 'from-blue-400 to-indigo-400',
          bgColor: '#3B82F6',
          emoji: '🎯',
          description: 'Set and achieve your goals!',
          sound: 'Goal setting! Dream big and make plans!'
        },
        {
          id: 'memory',
          label: 'Teamwork Match',
          icon: Grid3x3,
          route: '/game/memory',
          color: 'from-indigo-400 to-purple-400',
          bgColor: '#6366F1',
          emoji: '👥',
          description: 'Match teamwork and cooperation concepts!',
          sound: 'Teamwork makes the dream work!'
        },
        {
          id: 'story',
          label: 'Leadership Story',
          icon: BookHeart,
          route: '/game/story',
          color: 'from-violet-400 to-purple-400',
          bgColor: '#8B5CF6',
          emoji: '⭐',
          description: 'Stories about great leaders!',
          sound: 'Story time! Learn about inspiring leaders!'
        }
      ]
    }
  };

  // Default configuration for other topics
  const defaultConfig = {
    title: 'Choose Your Game',
    emoji: '🎮',
    subtitle: 'Pick your game style!',
    bgGradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    formats: [
      {
        id: 'quiz',
        label: 'Quiz Challenge',
        icon: Brain,
        route: '/game/quiz',
        color: 'from-green-400 to-emerald-400',
        bgColor: '#22C55E',
        emoji: '🧠',
        description: 'Answer questions and earn points!',
        sound: 'Quiz time! Answer questions and win!'
      },
      {
        id: 'puzzle',
        label: 'Puzzle Fun',
        icon: Puzzle,
        route: '/game/puzzle',
        color: 'from-blue-400 to-cyan-400',
        bgColor: '#3B82F6',
        emoji: '🧩',
        description: 'Solve puzzles and use your brain!',
        sound: 'Puzzle time! Can you solve it?'
      },
      {
        id: 'story',
        label: 'Story Adventure',
        icon: BookHeart,
        route: '/game/story',
        color: 'from-purple-400 to-violet-400',
        bgColor: '#8B5CF6',
        emoji: '📖',
        description: 'Learn through exciting stories!',
        sound: 'Story time! Once upon a time...'
      },
      {
        id: 'memory',
        label: 'Memory Match',
        icon: Grid3x3,
        route: '/game/memory',
        color: 'from-pink-400 to-rose-400',
        bgColor: '#EC4899',
        emoji: '🎴',
        description: 'Find matching pairs and remember!',
        sound: 'Memory game! Find the matching pairs!'
      }
    ]
  };

  // Get the current topic configuration
  const currentConfig = topicConfig[selectedSubject] || defaultConfig;

  const handleSelect = (format) => {
    setPlayingSound(format.id);
    playSound(format.sound);
    
    setTimeout(() => {
      setSelectedFormat(format.id);
      navigate(format.route);
    }, 1500);
  };

  // Determine back navigation based on age group
  const handleBack = () => {
    if (selectedAge === 'preschool') {
      navigate('/preschool-topics');
    } else {
      navigate('/subject-selection');
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-8" style={{
      background: currentConfig.bgGradient
    }}>
      <div className="max-w-6xl mx-auto">
        <Button
          onClick={handleBack}
          variant="ghost"
          className="mb-6 text-lg font-semibold hover:scale-110 transition-transform bg-white/50 hover:bg-white/70"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>

        <div className="text-center mb-8 md:mb-12">
          <div className="text-6xl md:text-8xl mb-4 animate-bounce">{currentConfig.emoji}</div>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-4" style={{
            fontFamily: '"Comic Neue", "Fredoka", cursive, sans-serif'
          }}>
            {currentConfig.title}
          </h1>
          <p className="text-xl md:text-2xl lg:text-3xl text-gray-700 font-bold">
            {currentConfig.subtitle} How do you want to play, {playerName}? 🎯
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 max-w-5xl mx-auto">
          {currentConfig.formats.map((format, index) => {
            const IconComponent = format.icon;
            const isHovered = hoveredFormat === format.id;
            const isPlaying = playingSound === format.id;
            return (
              <Card
                key={format.id}
                className={`cursor-pointer transition-all duration-300 overflow-hidden ${isPlaying ? 'scale-105' : 'hover:scale-105'} hover:shadow-2xl`}
                style={{
                  animationDelay: `${index * 0.1}s`,
                  borderRadius: '24px',
                  border: isHovered ? '4px solid #FFD700' : '3px solid transparent',
                  boxShadow: isHovered ? '0 20px 40px rgba(0,0,0,0.15)' : '0 4px 6px rgba(0,0,0,0.1)'
                }}
                onClick={() => handleSelect(format)}
                onMouseEnter={() => setHoveredFormat(format.id)}
                onMouseLeave={() => setHoveredFormat(null)}
              >
                <div 
                  className="h-32 md:h-40 flex items-center justify-center relative overflow-hidden"
                  style={{ backgroundColor: format.bgColor }}
                >
                  {/* Decorative circles */}
                  <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16"></div>
                    <div className="absolute bottom-0 right-0 w-24 h-24 bg-white rounded-full translate-x-12 translate-y-12"></div>
                  </div>
                  
                  {/* Emoji */}
                  <div className={`text-6xl md:text-7xl ${isHovered || isPlaying ? 'animate-bounce' : ''}`}>
                    {format.emoji}
                  </div>
                  
                  {/* Sound indicator */}
                  {isPlaying && (
                    <div className="absolute top-3 left-3 bg-white/90 rounded-full p-2 animate-pulse">
                      <Volume2 className="w-5 h-5 text-gray-700" />
                    </div>
                  )}
                  
                  {/* Icon Badge */}
                  <div className="absolute bottom-3 right-3 bg-white/90 rounded-full p-2 shadow-lg">
                    <IconComponent className="w-5 h-5 md:w-6 md:h-6" style={{ color: format.bgColor }} />
                  </div>
                </div>
                
                <div className="p-4 md:p-5 bg-white">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-2 text-center" style={{
                    fontFamily: '"Fredoka", "Comic Neue", cursive, sans-serif'
                  }}>
                    {format.label}
                  </h3>
                  <p className="text-sm md:text-base text-gray-600 text-center mb-4">{format.description}</p>
                  
                  <div
                    className={`w-full py-3 text-base md:text-lg font-bold text-center text-white transition-all duration-300 rounded-xl ${isPlaying ? 'animate-pulse' : ''}`}
                    style={{
                      background: `linear-gradient(135deg, ${format.bgColor}, ${format.bgColor}dd)`,
                      fontFamily: '"Fredoka", cursive'
                    }}
                  >
                    {isPlaying ? '🔊 Starting...' : "Play This! 🎮"}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Fun decorative elements */}
      </div>
    </div>
  );
};

export default GameFormatSelection;
